import React, { useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from 'react-native';

import {
  largeUpsCloneForCreate,
  largeUpsCreateDraft,
  largeUpsEnsureNameForCreate,
} from './subsections/largeUpsEngineBindings';
import { LargeUpsEngineEditFormBody } from './subsections/LargeUpsEngineEditFormBody';
import {
  isLargeUpsPersistedProps,
  type LargeUpsProps,
  type LargeUpsPersistedProps,
  type LargeUpsInitialProps,
} from './useLargeUps';
import { Eye, EyeOff } from '../../../components/icons';
import { buildLegacyLargeUpsFromRequestFields } from '../../../components/largeups/largeUpsListUtils';
import { PersistedStepHeader } from '../../../components/PersistedStepHeader';
import { useRequestScope } from '../../../contexts/RequestScopeContext';
import { useLargeUpsConfigurations } from '../../../hooks/queries/useLargeUpsConfigurations';
import {
  descriptorInstanceToLargeUps,
  largeUpsToDescriptorInstance,
} from '../../../services/descriptorEditSessionFlush';
import { shouldShowLargeUpsAdditionalInformationInUnitEditor } from '../../../utils/largeUpsAdditionalInformation';
import { resolveEnabledStepIds } from '../../../utils/resolveRequestFormSteps';
import {
  DescriptorEngineSection,
  type DescriptorInstance,
} from '../../sections/descriptorEngineAdapter';
import { LARGE_UPS_DESCRIPTOR } from '../../sections/largeUps.descriptor';

import type { LargeUpsConfigurationsSaveHandle } from './subsections/largeUpsConfigurationsSaveHandle';
import type { RepeatableInstanceEditFormRenderProps } from '../../../components/repeatableSections/repeatableInstanceEditFormTypes';
import type { RepeatableInstanceSaveHandle } from '../../../components/repeatableSections/repeatableInstanceSaveHandle';
import type { LargeUpsConfiguration } from '../../../types/largeUps';

const LargeUpsInitialLayout: React.FC<LargeUpsInitialProps> = ({
  formData,
  updateFormData,
  requestId,
  disabled = false,
  attachmentsBySection = {},
  onAttachFiles,
  onDeleteAttachment,
  isEditing = false,
  selectedFiles = {},
  onFilesSelected,
  offlineMode = false,
  showFullDetails = true,
  enabledStepIds: enabledStepIdsProp,
  registerLargeUpsSaveHandle,
  onLargeUpsEditorActiveChange,
}) => {
  const requestScope = useRequestScope();
  const enabledStepIds =
    enabledStepIdsProp ??
    requestScope?.enabledStepIds ??
    resolveEnabledStepIds(
      (formData as { enabledStepIds?: string[] | null }).enabledStepIds,
    );
  const requestStage = String(
    formData.sysRequestStage ?? formData.stage ?? 'DRAFT',
  );
  const showAdditionalInformationInEditor =
    shouldShowLargeUpsAdditionalInformationInUnitEditor({
      enabledStepIds,
    });

  // #997 Story 6.5 — the create/draft layout runs on the descriptor engine
  // (mirror Small UPS 6.4). The engine owns the list/edit-session/save/flush/
  // attachment lifecycle (6.1 per-unit scoping + 6.2 temp→real remap now run
  // live); Large UPS supplies its bespoke view (`LargeUpsEngineEditFormBody` →
  // `LargeUpsEditFormContent`) and its proven draft/name/clone semantics
  // (`largeUpsEngineBindings`) through the engine's per-section seams, so the
  // user-visible editor is unchanged. The draft list stays parent-owned in
  // `formData.largeUpsConfigurations`.
  const draftLargeUps =
    (formData.largeUpsConfigurations as LargeUpsConfiguration[] | undefined) ??
    [];
  const draftInstances = useMemo(
    () =>
      draftLargeUps.map(
        unit => largeUpsToDescriptorInstance(unit) as DescriptorInstance,
      ),
    [draftLargeUps],
  );
  const requestIdNum = requestId ? Number.parseInt(requestId, 10) || 0 : 0;
  const handleDraftInstancesChange = useCallback(
    (instances: DescriptorInstance[]) =>
      updateFormData(
        'largeUpsConfigurations',
        instances.map(instance =>
          descriptorInstanceToLargeUps(
            instance,
            requestIdNum,
            LARGE_UPS_DESCRIPTOR,
          ),
        ),
      ),
    [updateFormData, requestIdNum],
  );

  // Engine handle → bespoke LargeUpsConfigurationsSaveHandle (CreateRequest's
  // footer-save/dismiss/scope-removal bridge keeps its existing contract).
  const registerInstanceSaveHandle = useMemo(() => {
    if (!registerLargeUpsSaveHandle) {
      return undefined;
    }
    return (handle: RepeatableInstanceSaveHandle | null) => {
      if (!handle) {
        registerLargeUpsSaveHandle(null);
        return;
      }
      const largeUpsHandle: LargeUpsConfigurationsSaveHandle = {
        isLargeUpsEditorOpen: handle.isInstanceEditorOpen,
        hasUnsavedLargeUpsEditor: handle.hasUnsavedInstanceEditor,
        commitOpenLargeUpsEditor: handle.commitOpenInstanceEditor,
        saveUnsavedLargeUpsIfNeeded: async () => {
          if (
            !handle.isInstanceEditorOpen() ||
            !handle.hasUnsavedInstanceEditor()
          ) {
            return true;
          }
          return handle.commitOpenInstanceEditor();
        },
        hasPendingLargeUpsChanges: () =>
          handle.hasPendingListChanges?.() ?? false,
        flushPendingLargeUpsChanges: async () => {
          await handle.flushPendingListChanges?.();
        },
        tryDismissLargeUpsEditor: handle.tryDismissInstanceEditor,
        discardOpenLargeUpsEditor: handle.discardOpenInstanceEditor,
        // Draft list is parent-owned (formData.largeUpsConfigurations) — scope
        // removal strips it there; the in-memory session reset is the
        // persisted-path concern.
        resetSessionAfterScopeRemoval: () => {},
      };
      registerLargeUpsSaveHandle(largeUpsHandle);
    };
  }, [registerLargeUpsSaveHandle]);

  const editBody = useCallback(
    (props: RepeatableInstanceEditFormRenderProps) => (
      <LargeUpsEngineEditFormBody
        {...props}
        showAdditionalInformation={showAdditionalInformationInEditor}
        showFullDetails={showFullDetails}
      />
    ),
    [showAdditionalInformationInEditor, showFullDetails],
  );

  return (
    <View style={largeUpsInitialStyles.container}>
      <PersistedStepHeader
        title="Large UPS"
        uomSlot={null}
        actionsSlot={null}
      />

      <DescriptorEngineSection
        descriptor={LARGE_UPS_DESCRIPTOR}
        requestId={requestId}
        isEditing={isEditing || !disabled}
        disabled={disabled}
        requestStage={requestStage}
        showFullDetails={showFullDetails}
        draftInstances={draftInstances}
        onDraftInstancesChange={handleDraftInstancesChange}
        legacyRequestFields={formData}
        attachmentsBySection={attachmentsBySection}
        onAttachFiles={onAttachFiles}
        onDeleteAttachment={onDeleteAttachment}
        selectedFiles={selectedFiles}
        onFilesSelected={onFilesSelected}
        offlineMode={offlineMode}
        editBody={editBody}
        createDraft={largeUpsCreateDraft}
        ensureNameForCreate={largeUpsEnsureNameForCreate}
        cloneForCreate={largeUpsCloneForCreate}
        registerInstanceSaveHandle={registerInstanceSaveHandle}
        onInstanceEditorActiveChange={onLargeUpsEditorActiveChange}
        testID="large-ups-configurations"
      />
    </View>
  );
};

const LargeUpsPersistedLayout: React.FC<LargeUpsPersistedProps> = ({
  request,
  isEditing,
  editedData,
  showFullDetails = true,
  showViewToggle,
  isAdvancedView,
  onToggleView,
  attachmentsBySection = {},
  onAttachFiles,
  onDeleteAttachment,
  selectedFiles = {},
  onFilesSelected,
  offlineMode = false,
  onCaptionChange,
  pendingCaptionEdits = {},
  enabledStepIds: enabledStepIdsProp,
  registerInstanceSaveHandle,
  applyEditedDataPatch,
  largeUpsConfigurationsDirty = false,
  onLargeUpsEditorActiveChange,
}) => {
  const requestScope = useRequestScope();
  const enabledStepIds =
    enabledStepIdsProp ??
    requestScope?.enabledStepIds ??
    resolveEnabledStepIds(request.enabledStepIds);
  const showAdditionalInformationInEditor =
    shouldShowLargeUpsAdditionalInformationInUnitEditor({
      enabledStepIds,
    });
  const requestIdStr = String(request.id ?? '');
  const fieldDisabled = !isEditing;
  const stage = String(request.stage || request.sysRequestStage || 'DRAFT');
  const isOfflineRequest =
    request.id != null && String(request.id).startsWith('o-');

  const pdfFormData = useMemo(
    () => ({
      ...(request as unknown as Record<string, unknown>),
      ...(isEditing ? editedData : {}),
    }),
    [request, editedData, isEditing],
  );

  // #997 Story 6.5 — the persisted layout runs on the descriptor engine (mirror
  // Small UPS). Host-boundary display-list resolution: while the user has Large
  // UPS edits this session, `editedData.largeUpsConfigurations` is authoritative
  // (even when empty — deleted-all must not resurrect); otherwise units query >
  // request embed > legacy flat-unit synthesis.
  const { data: queryUnits = [] } = useLargeUpsConfigurations(
    requestIdStr,
    true,
  );
  const editedUnits = editedData?.largeUpsConfigurations;
  const effectiveUnits = useMemo((): LargeUpsConfiguration[] => {
    if (
      isEditing &&
      largeUpsConfigurationsDirty &&
      Array.isArray(editedUnits)
    ) {
      return editedUnits as LargeUpsConfiguration[];
    }
    if (queryUnits.length > 0) {
      return queryUnits;
    }
    const embed = pdfFormData.largeUpsConfigurations;
    if (Array.isArray(embed) && embed.length > 0) {
      return embed as LargeUpsConfiguration[];
    }
    const legacyUnit = buildLegacyLargeUpsFromRequestFields(
      pdfFormData,
      requestIdStr,
    );
    return legacyUnit ? [legacyUnit] : [];
  }, [
    isEditing,
    largeUpsConfigurationsDirty,
    editedUnits,
    queryUnits,
    pdfFormData,
    requestIdStr,
  ]);

  const legacyRequestFields = useMemo(
    () => ({ ...pdfFormData, largeUpsConfigurations: effectiveUnits }),
    [pdfFormData, effectiveUnits],
  );

  const editBody = useCallback(
    (props: RepeatableInstanceEditFormRenderProps) => (
      <LargeUpsEngineEditFormBody
        {...props}
        showAdditionalInformation={showAdditionalInformationInEditor}
        showFullDetails={showFullDetails}
      />
    ),
    [showAdditionalInformationInEditor, showFullDetails],
  );

  return (
    <View style={largeUpsPersistedStyles.container}>
      <PersistedStepHeader
        title="Large UPS"
        uomSlot={null}
        actionsSlot={
          showViewToggle && onToggleView ? (
            <TouchableOpacity
              style={largeUpsPersistedStyles.viewToggleButton}
              onPress={onToggleView}
            >
              {isAdvancedView ? (
                <Eye size={Platform.OS === 'web' ? 18 : 16} color="#666" />
              ) : (
                <EyeOff size={Platform.OS === 'web' ? 18 : 16} color="#666" />
              )}
              <Text style={largeUpsPersistedStyles.viewToggleText}>
                {isAdvancedView ? 'Advanced' : 'Basic'}
              </Text>
            </TouchableOpacity>
          ) : null
        }
      />

      <DescriptorEngineSection
        descriptor={LARGE_UPS_DESCRIPTOR}
        requestId={requestIdStr}
        isEditing={isEditing}
        // #997 QA-C: keep view-mode rows drillable; mutations stay gated by
        // isEditing/listReadOnly, so do NOT restore fieldDisabled (=!isEditing) here.
        disabled={false}
        requestStage={stage}
        showFullDetails={showFullDetails}
        legacyRequestFields={legacyRequestFields}
        applyEditedDataPatch={applyEditedDataPatch}
        attachmentsBySection={attachmentsBySection}
        onAttachFiles={onAttachFiles}
        onDeleteAttachment={onDeleteAttachment}
        selectedFiles={selectedFiles}
        onFilesSelected={onFilesSelected}
        offlineMode={isOfflineRequest || offlineMode}
        onCaptionChange={onCaptionChange}
        pendingCaptionEdits={pendingCaptionEdits}
        editBody={editBody}
        createDraft={largeUpsCreateDraft}
        ensureNameForCreate={largeUpsEnsureNameForCreate}
        cloneForCreate={largeUpsCloneForCreate}
        registerInstanceSaveHandle={registerInstanceSaveHandle}
        onInstanceEditorActiveChange={onLargeUpsEditorActiveChange}
        testID="large-ups-configurations"
      />
    </View>
  );
};

const largeUpsInitialStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

const largeUpsPersistedStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
  viewToggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: Platform.OS === 'web' ? 12 : 8,
    paddingVertical: Platform.OS === 'web' ? 6 : 4,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f8f9fa',
  },
  viewToggleText: {
    fontSize: Platform.OS === 'web' ? 13 : 11,
    fontWeight: '500',
    color: '#666',
  },
});

export const LargeUps: React.FC<LargeUpsProps> = props =>
  isLargeUpsPersistedProps(props) ? (
    <LargeUpsPersistedLayout {...props} />
  ) : (
    <LargeUpsInitialLayout {...props} />
  );

LargeUps.displayName = 'LargeUps';

/** @deprecated Test / caption suites — compose path only */
export const LargeUpsStep = LargeUpsInitialLayout;
/** @deprecated Test / caption suites — persisted request detail branch */
export const LargeUpsInRequest = LargeUpsPersistedLayout;
